// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {
	partsExclude,
	ROOM_OUTFIT_SLOTS,
	type RoomCharacterPart,
	type RoomOutfitItemType,
	type RoomOutfitSlot,
} from '@fluxer/constants/src/RoomOutfit';
import {VOICE_ROOM_CHARACTER_PART_MANIFEST as MANIFEST} from '@fluxer/constants/src/VoiceRoomCharacterPartManifest';

export const ROOM_PART_MANIFEST = MANIFEST;

// Item IDs worn per slot; null leaves the slot to the preset.
export type RoomOutfit = Record<RoomOutfitSlot, string | null>;
export const EMPTY_ROOM_OUTFIT: RoomOutfit = Object.freeze(
	Object.fromEntries(ROOM_OUTFIT_SLOTS.map((slot) => [slot, null])) as RoomOutfit,
);

const SLOT_OF_TYPE = new Map<string, RoomOutfitSlot>(ROOM_OUTFIT_SLOTS.map((slot) => [`room_${slot}`, slot]));

export function isRoomPartType(type: string): type is RoomOutfitItemType {
	return SLOT_OF_TYPE.has(type);
}

export function roomSlotOf(type: RoomOutfitItemType): RoomOutfitSlot {
	return SLOT_OF_TYPE.get(type)!;
}

// Whether this client ships a part manifest at all; without one the whole-character picker stays.
export function hasRoomOutfits(): boolean {
	return Object.hasOwn(MANIFEST.families, MANIFEST.default_family);
}

export function roomPart(key: string | null | undefined): RoomCharacterPart | null {
	return key && Object.hasOwn(MANIFEST.parts, key) ? MANIFEST.parts[key]! : null;
}

// A part this client can draw, in the slot its item type names.
export function isWearableRoomPart(item: {type: string; model_key?: string | null}): boolean {
	return isRoomPartType(item.type) && roomPart(item.model_key)?.slot === roomSlotOf(item.type);
}

const roomFileUrl = (file: string) => `${RuntimeConfig.staticCdnEndpoint}/rooms/${file}`;

export function roomPartPreviewUrl(key: string | null | undefined): string | null {
	const part = roomPart(key);
	return part ? roomFileUrl(part.preview) : null;
}

export function roomPresetPreviewUrl(key: string): string | null {
	return Object.hasOwn(MANIFEST.presets, key) ? roomFileUrl(MANIFEST.presets[key]!.preview) : null;
}

// The outfit after wearing itemId in slot. Worn parts that clash with it come off: the newest pick wins, where the
// shared resolver would keep the earlier slot.
export function wearRoomPart(
	outfit: RoomOutfit,
	slot: RoomOutfitSlot,
	itemId: string | null,
	keyOf: (itemId: string) => string | null,
): RoomOutfit {
	const next = {...outfit, [slot]: itemId};
	const picked = itemId ? roomPart(keyOf(itemId)) : null;
	if (!picked) return next;
	for (const other of ROOM_OUTFIT_SLOTS) {
		const otherId = next[other];
		const part = other !== slot && otherId ? roomPart(keyOf(otherId)) : null;
		if (part && partsExclude(picked, part)) next[other] = null;
	}
	return next;
}

// wearRoomPart by part keys: the worn keys with key put on last; the part in its slot and any it clashes with come off.
export function wearRoomPartKey(worn: ReadonlyArray<string>, key: string): Array<string> {
	const picked = roomPart(key);
	if (!picked) return [...worn];
	const kept = worn.filter((other) => {
		const part = roomPart(other);
		return part !== null && part.slot !== picked.slot && !partsExclude(picked, part);
	});
	return [...kept, key];
}

interface PresetItem {
	id: string;
	wearable: boolean;
}

// A preset as an outfit of catalog items: every slot it leaves out is emptied. locked names its parts the account
// cannot wear (not owned, premium without premium, or no catalog item at all).
export function presetOutfit(
	presetKey: string,
	itemFor: (partKey: string) => PresetItem | null,
): {outfit: RoomOutfit; locked: Array<string>} | null {
	if (!Object.hasOwn(MANIFEST.presets, presetKey)) return null;
	const outfit = {...EMPTY_ROOM_OUTFIT};
	const locked: Array<string> = [];
	for (const key of MANIFEST.presets[presetKey]!.parts) {
		const part = roomPart(key);
		const item = itemFor(key);
		if (part && item?.wearable) outfit[part.slot] = item.id;
		else locked.push(key);
	}
	return {outfit, locked};
}
